import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAdminUser,
  useAdminUserDemographics,
  useAdminUserRoles,
  useUpdateAdminUserProfile,
  type UserProfileDraft,
} from '@/features/admin-users/api';
import { UserRecordView } from '@/features/user-record/UserRecordView';

/**
 * Tenant-plane user page (PTV2-1451) at /admin/users/:uuid — the first-class view for a tenant user.
 * Feeds the shared UserRecordView the tenant hooks. Linked identities are omitted for now (tenant-reach
 * federation is a deferred fast-follow — the panel appears once that endpoint exists).
 */
export function AdminUserDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { uuid = '' } = useParams<{ uuid: string }>();

  const userQuery = useAdminUser(uuid);
  const rolesQuery = useAdminUserRoles(uuid);
  const demographicsQuery = useAdminUserDemographics(uuid);
  const update = useUpdateAdminUserProfile();

  if (userQuery.isPending) {
    return <Skeleton className="m-8 h-40 w-full max-w-xl rounded-md" />;
  }
  if (userQuery.isError) {
    return (
      <div className="max-w-xl p-8">
        <FetchErrorCallout
          error={userQuery.error}
          heading={t('userRecord.error.heading')}
          message={t('userRecord.error.message')}
          codeLabel={t('userRecord.error.codeLabel')}
          onRetry={() => {
            userQuery.refetch().catch(() => undefined);
          }}
          retryLabel={t('userRecord.error.retry')}
        />
      </div>
    );
  }

  const user = userQuery.data;
  const displayName = user.fullName.length > 0 ? user.fullName : user.name;

  function saveProfile(draft: UserProfileDraft): void {
    update.mutate({ uuid, draft });
  }

  return (
    <UserRecordView
      displayName={displayName}
      username={user.name}
      email={user.email}
      status={user.status}
      roles={rolesQuery.data ?? user.roles}
      breadcrumbs={[
        { label: t('shell.topNav.admin'), to: '/admin' },
        { label: t('routes.admin.users.heading'), to: '/admin/users' },
        { label: displayName },
      ]}
      demographics={demographicsQuery.data}
      demographicsLoading={demographicsQuery.isPending}
      canEdit
      saving={update.isPending}
      saveError={update.error}
      onSaveProfile={saveProfile}
      identities={null}
    />
  );
}
